/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Client-side UI - Weapon selection modal
 * WeaponSelectionModal.tsx - Modal for selecting primary weapon with stats display
 */

import { WEAPON_CONFIGS, WeaponType, getWeaponStats } from "@game/config";
import type React from "react";
import { DatabaseSaveError, loadoutService } from "./loadout.service";

// ============================================================================
// TYPES
// ============================================================================

/**
 * Props for WeaponSelectionModal component
 */
interface WeaponSelectionModalProps {
  /** Whether modal is visible */
  readonly isOpen: boolean;
  /** Callback to close modal */
  readonly onClose: () => void;
  /** Currently selected weapon (for highlighting) */
  readonly currentWeapon: WeaponType;
  /** Callback when weapon is selected and saved */
  readonly onWeaponSelected: (weapon: WeaponType) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * All available weapons in display order (2x2 grid)
 */
const AVAILABLE_WEAPONS: readonly WeaponType[] = [
  WeaponType.DEFAULT,
  WeaponType.SHOTGUN,
  WeaponType.SNIPER,
  WeaponType.MACHINEGUN,
];

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * WeaponSelectionModal - Modal for selecting primary weapon
 *
 * **Purpose:**
 * - Display all weapons in 2x2 grid with detailed stats
 * - Allow selection and save to database + localStorage
 * - Highlight currently selected weapon
 * - Graceful error handling with fallback to localStorage
 *
 * **Design:**
 * - Retro-terminal military aesthetic (dark + green)
 * - Click outside to close (modal overlay)
 * - Each weapon card shows: name, damage, fire rate, speed, range, spread, pellets
 * - Selected weapon highlighted with accent border
 *
 * **Error Handling:**
 * - Database save failures fall back to localStorage
 * - All errors logged to console
 * - User sees no error UI (seamless fallback)
 *
 * **Storage Strategy:**
 * 1. Attempt database save (logged-in users)
 * 2. Fallback to localStorage (always succeeds)
 * 3. Notify parent via onWeaponSelected callback
 * 4. Close modal
 *
 * @example
 * ```tsx
 * <WeaponSelectionModal
 *   isOpen={modalOpen}
 *   onClose={() => setModalOpen(false)}
 *   currentWeapon={WeaponType.DEFAULT}
 *   onWeaponSelected={(weapon) => setCurrentWeapon(weapon)}
 * />
 * ```
 */
export const WeaponSelectionModal: React.FC<WeaponSelectionModalProps> = ({
  isOpen,
  onClose,
  currentWeapon,
  onWeaponSelected,
}) => {
  /**
   * Handle weapon selection
   *
   * **Flow:**
   * 1. Attempt database save
   * 2. On database failure, fall back to localStorage
   * 3. Notify parent component
   * 4. Close modal
   *
   * **Error Handling:**
   * - Database errors logged, localStorage fallback used
   * - Storage errors logged, but modal still closes
   * - User experience remains smooth regardless of errors
   */
  const handleWeaponSelect = async (weapon: WeaponType): Promise<void> => {
    try {
      await loadoutService.saveToDatabase(weapon);
      onWeaponSelected(weapon);
      onClose();
    } catch (error) {
      if (error instanceof DatabaseSaveError) {
        console.warn(
          "[WeaponSelectionModal] Database save failed, localStorage fallback already applied",
        );
      } else {
        console.error("[WeaponSelectionModal] Unexpected error:", error);
      }

      onWeaponSelected(weapon);
      onClose();
    }
  };

  /**
   * Handle overlay click (click outside modal to close)
   */
  const handleOverlayClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  /**
   * Handle escape key press to close modal
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: Modal backdrop uses div with role="button" pattern
    <div
      className="weapon-modal-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={-1}
      aria-label="Close modal overlay"
    >
      <div className="weapon-modal" role="dialog" aria-modal="true">
        <div className="weapon-modal-header">
          <h2 className="weapon-modal-title">SELECT PRIMARY WEAPON</h2>
          <button
            type="button"
            className="weapon-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="weapon-grid">
          {AVAILABLE_WEAPONS.map((weaponType) => {
            const stats = getWeaponStats(weaponType);
            const config = WEAPON_CONFIGS[weaponType];
            const isSelected = weaponType === currentWeapon;

            return (
              // biome-ignore lint/a11y/useSemanticElements: Weapon card uses div with role="button" for complex layout with stats
              <div
                key={weaponType}
                className={`weapon-card ${isSelected ? "weapon-card-selected" : ""}`}
                onClick={() => handleWeaponSelect(weaponType)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleWeaponSelect(weaponType);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Select ${stats.name} weapon`}
              >
                <div className="weapon-card-name">{stats.name}</div>

                <div className="weapon-card-stats">
                  <div className="weapon-stat">
                    <span className="weapon-stat-label">DMG:</span>
                    <span className="weapon-stat-value">{config.damage}</span>
                  </div>
                  <div className="weapon-stat">
                    <span className="weapon-stat-label">RATE:</span>
                    <span className="weapon-stat-value">
                      {stats.fireRateRPM} RPM
                    </span>
                  </div>
                  <div className="weapon-stat">
                    <span className="weapon-stat-label">SPD:</span>
                    <span className="weapon-stat-value">
                      {config.bulletSpeed}
                    </span>
                  </div>
                  <div className="weapon-stat">
                    <span className="weapon-stat-label">RNG:</span>
                    <span className="weapon-stat-value">{stats.maxRange}</span>
                  </div>
                  {config.spreadDegrees > 0 && (
                    <div className="weapon-stat">
                      <span className="weapon-stat-label">SPREAD:</span>
                      <span className="weapon-stat-value">
                        {config.spreadDegrees}°
                      </span>
                    </div>
                  )}
                  {config.pelletsPerShot > 1 && (
                    <div className="weapon-stat">
                      <span className="weapon-stat-label">PELLETS:</span>
                      <span className="weapon-stat-value">
                        {config.pelletsPerShot}
                      </span>
                    </div>
                  )}
                </div>

                <div className="weapon-card-dps">
                  DPS: {stats.dps.toFixed(1)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
